import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import PolicyLayout from "./PolicyLayout";
import "../css/terms.scss";

const terms = [
  {
    title: "Agreement and store operator",
    content: (
      <>
        <p>
          These terms govern access to this site and purchases from the online
          store operating under the {siteConfig.brandName} brand from{" "}
          {siteConfig.businessLocation.countryShortName}. The business address is{" "}
          {siteConfig.contact.address}, {siteConfig.businessLocation.countryName}.
          By using the site or submitting an order, you agree to these terms and
          the policies linked here. If you do not agree, do not place an order.
        </p>
        <p>
          A specific product, promotion, or written offer may contain additional
          terms. Those specific terms control for that subject where they do not
          conflict with mandatory law.
        </p>
      </>
    ),
  },
  {
    title: "Eligibility and accurate information",
    content: (
      <p>
        You must have legal capacity to make the purchase or the authorization
        of a parent or guardian. Provide current, complete, and accurate contact,
        delivery, and payment information. You may not impersonate another person
        or use a payment method without permission.
      </p>
    ),
  },
  {
    title: "Products, images, and availability",
    content: (
      <>
        <p>
          We aim to present product names, specifications, colors, materials,
          dimensions, compatibility, prices, and photographs accurately. Screens,
          natural materials, production batches, and manual measurements may
          cause reasonable differences. Such a difference does not remove a
          remedy where an item is materially misdescribed or defective.
        </p>
        <p>
          Stock is not reserved until an order is accepted. Quantity limits and
          destination restrictions may apply. We may correct an obvious error
          before acceptance and will offer the option to proceed on corrected
          terms or receive a refund where payment was captured.
        </p>
      </>
    ),
  },
  {
    title: "Prices, currency, taxes, and duties",
    content: (
      <p>
        Store prices and totals are shown in USD. Delivery charges are shown
        before payment. Import duties, taxes, brokerage, and other destination
        charges are the recipient&apos;s responsibility unless checkout expressly
        states they are included. Your bank may apply conversion or
        foreign-transaction charges that we do not control.
      </p>
    ),
  },
  {
    title: "Orders and contract formation",
    content: (
      <>
        <p>
          Submitting an order is an offer to purchase. A payment screen or
          automated receipt only acknowledges receipt. We accept the order, and a
          contract is formed, when we send an order confirmation after payment
          authorization and stock checks.
        </p>
        <p>
          We may refuse or cancel an order for a stock shortage, obvious pricing
          or description error, delivery restriction, suspected fraud, prohibited
          transaction, or unauthorized resale. Captured amounts for a cancelled
          order will be refunded to the original payment method.
        </p>
        <Link className="editorial-text-link" to="/orders">
          Read the order policy <span aria-hidden="true">→</span>
        </Link>
      </>
    ),
  },
  {
    title: "Payment",
    content: (
      <>
        <p>
          Payment credentials are entered with a third-party payment provider.
          Its terms, privacy notice, authentication, and fraud controls also
          apply to that service. We may request reasonable verification but will
          never ask you to email a full card number, security code, or password.
        </p>
        <Link className="editorial-text-link" to="/payments">
          Read the payment policy <span aria-hidden="true">→</span>
        </Link>
      </>
    ),
  },
  {
    title: "Delivery, customs, and risk",
    content: (
      <>
        <p>
          Processing and transit times are estimates, not guarantees. Carriers,
          customs, weather, address issues, and events outside reasonable control
          can cause delay. Responsibility for loss or damage transfers only as
          provided by applicable law; no wording here removes a mandatory remedy
          for a parcel lost or damaged before that point.
        </p>
        <Link className="editorial-text-link" to="/shipping">
          Read the shipping policy <span aria-hidden="true">→</span>
        </Link>
      </>
    ),
  },
  {
    title: "Cancellations, returns, and refunds",
    content: (
      <>
        <p>
          Cancellation is available before dispatch where operationally possible.
          Eligible unused goods may be returned under our published return
          process. Exclusions for opened consumables, personalized goods, or final
          sale items do not apply when a remedy is mandatory for defective,
          unsafe, or misdescribed goods.
        </p>
        <Link className="editorial-text-link" to="/returns">
          Read the returns and refunds policy <span aria-hidden="true">→</span>
        </Link>
      </>
    ),
  },
  {
    title: "Product safety and intended use",
    content: (
      <p>
        Follow product labels, warnings, age guidance, and maker instructions.
        Unless expressly stated, stationery and art products are not toys, food,
        cosmetics, or medical devices. Keep blades, sharp points, small parts,
        pigments, inks, and adhesives away from children and pets as appropriate.
        Stop using and report a product that appears unsafe.
      </p>
    ),
  },
  {
    title: "Promotions",
    content: (
      <p>
        A promotion is governed by its published dates, eligibility, stock,
        destination, minimum spend, and exclusions. Unless stated otherwise,
        offers cannot be combined, applied after purchase, exchanged for cash, or
        used to evade quantity limits. See the{" "}
        <Link to="/promotions">promotions policy</Link> for full rules.
      </p>
    ),
  },
  {
    title: "Intellectual property",
    content: (
      <p>
        Original {siteConfig.brandName} text, design, graphics, and catalog
        presentation are owned by or licensed to the store operator. Third-party
        product names, marks, designs, and materials remain the property of their
        respective owners and are used only for identification where applicable.
        Personal, non-commercial viewing and record-keeping are permitted; other
        reproduction or exploitation requires permission or a lawful exception.
      </p>
    ),
  },
  {
    title: "Acceptable use and site security",
    content: (
      <p>
        Use the site lawfully. Do not introduce malicious code, attempt
        unauthorized access, evade security or purchase limits, interfere with
        availability, manipulate price or inventory, misuse another person&apos;s
        data, or extract protected content for unauthorized commercial use. We
        may restrict activity reasonably believed to threaten customers, the
        site, or applicable law.
      </p>
    ),
  },
  {
    title: "Third-party services and links",
    content: (
      <p>
        Payment, delivery, and linked websites may be operated independently.
        Their terms and privacy practices apply to their services. We are not
        responsible for third-party content or conduct outside our reasonable
        control, but this does not remove responsibility that applicable law
        places on us for providers acting on our behalf.
      </p>
    ),
  },
  {
    title: "Disclaimers and limitation of liability",
    content: (
      <>
        <p>
          The site may occasionally be unavailable or contain an error. To the
          maximum extent permitted by law, we are not liable for indirect or
          consequential losses that were not reasonably foreseeable when the
          order was accepted, or for loss caused solely by misuse, unauthorized
          device access, or events outside reasonable control.
        </p>
        <p>
          Nothing in these terms excludes or limits a liability or remedy that
          cannot lawfully be excluded, including applicable rights concerning
          defective, unsafe, or misdescribed goods, fraud, or personal injury
          where exclusion is prohibited.
        </p>
      </>
    ),
  },
  {
    title: "Events outside reasonable control",
    content: (
      <p>
        We are not in breach for delay caused by an event we could not reasonably
        prevent, including carrier disruption, severe weather, natural disaster,
        labor disruption, network outage, epidemic, government action, or import
        restriction. We will take reasonable steps to reduce the effect and will
        offer any cancellation or refund required by law.
      </p>
    ),
  },
  {
    title: "Governing law and disputes",
    content: (
      <p>
        These terms are governed by the laws of Hong Kong, without depriving a
        consumer of mandatory protections that apply in the consumer&apos;s place of
        residence. Courts with lawful jurisdiction may hear a dispute. Before
        formal proceedings, we encourage you to use our{" "}
        <Link to="/complaints">complaints process</Link>, but it does not prevent
        either party from seeking urgent relief or exercising a legal right.
      </p>
    ),
  },
  {
    title: "Changes, severability, and no waiver",
    content: (
      <p>
        The version in effect when an order is placed applies to that order. We
        may update terms for future use of the site by posting a revised date. If
        one provision is unenforceable, the remainder continues to the extent
        legally possible. A delay in enforcing a provision is not a waiver of it.
      </p>
    ),
  },
];

const Terms = () => (
  <PolicyLayout
    eyebrow="Site information"
    title="Terms of service"
    lede={`The terms for using ${siteConfig.brandName} and purchasing products from this online store.`}
  >
    <div className="terms-summary">
      <strong>The short version</strong>
      <p>
        Provide accurate information, use the site lawfully, review your order,
        and read the policies relevant to payment, delivery, returns, privacy,
        safety, and promotions before buying.
      </p>
    </div>
    <div className="terms-list">
      {terms.map(({ title, content }, index) => (
        <section className="policy-section term-section" key={title}>
          <span className="term-number">
            {String(index + 1).padStart(2, "0")}
          </span>
          <div>
            <h2>{title}</h2>
            {content}
          </div>
        </section>
      ))}
    </div>
  </PolicyLayout>
);

export default Terms;
